import { useState, useEffect, useCallback } from "react";
import { RefreshCw, Loader2, Timer, CalendarClock } from "lucide-react";
import { ContainerCard } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import useApiMethods from "../../lib/useApiMethods";
import { AD_TIME_LAG_REPORT_URL } from "../../constants/api";
import ToastMsg from "../../components/common/ToastMsg";
import { usePersistedState } from "../../hooks/usePersistedState";
import { cn } from "../../utils/tailwindUtils";
import {
    AdFilterBar, AttributionBanner, EmptyState, StatTile, money, num, pct,
    resolveDateRange,
    StatusPill, isDelivering,
    StatusFilterSelect,
} from "./adReportKit";

const days = (v) => (v == null ? "—" : `${v}d`);

/** Lead → demo → won as a proportional timeline, so slow ads read as slow. */
const LagBar = ({ toDemo, toWon, max }) => {
    if (toWon == null && toDemo == null) {
        return <span className="text-[11px] text-muted-foreground">no closed deals yet</span>;
    }
    const scale = max || 1;
    return (
        <div className="relative h-4 w-full min-w-[110px] rounded-sm bg-muted overflow-hidden">
            {toWon != null && (
                <div className="absolute inset-y-0 left-0 bg-emerald-500/25"
                    style={{ width: `${Math.min((toWon * 100) / scale, 100)}%` }} />
            )}
            {toDemo != null && (
                <div className="absolute inset-y-0 left-0 bg-violet-500/60"
                    style={{ width: `${Math.min((toDemo * 100) / scale, 100)}%` }} />
            )}
            {toWon != null && (
                <div className="absolute inset-y-0 w-0.5 bg-emerald-600"
                    style={{ left: `${Math.min((toWon * 100) / scale, 100)}%` }} />
            )}
            <div className="absolute inset-0 flex items-center justify-end pr-1.5">
                <span className="text-[9px] font-medium text-foreground tabular-nums">
                    {toDemo != null && `${toDemo}d demo`}{toDemo != null && toWon != null && " · "}
                    {toWon != null && `${toWon}d won`}
                </span>
            </div>
        </div>
    );
};

export default function AdTimeLagReport() {
    const { fetch } = useApiMethods();

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const [datePreset, setDatePreset] = usePersistedState("ad_tl_preset", "last_90");
    const [customFrom, setCustomFrom] = usePersistedState("ad_tl_from", "");
    const [customTo, setCustomTo] = usePersistedState("ad_tl_to", "");
    const [accountId, setAccountId] = usePersistedState("ad_tl_account", "");
    const [statusFilter, setStatusFilter] = usePersistedState("ad_tl_status", "");
    const [level, setLevel] = usePersistedState("ad_tl_level", "ad");

    const load = useCallback(() => {
        if (datePreset === "custom" && (!customFrom || !customTo)) return;
        setLoading(true);
        const { from_date, to_date } = resolveDateRange(datePreset, customFrom, customTo);
        const p = new URLSearchParams({ level });
        if (from_date) p.append("from_date", from_date);
        if (to_date) p.append("to_date", to_date);
        if (accountId) p.append("account_id", accountId);
        if (statusFilter) p.append("status", statusFilter);
        fetch(`${AD_TIME_LAG_REPORT_URL}?${p.toString()}`)
            .then((res) => {
                if (res?.status === "success") setData(res);
                else ToastMsg("error", res?.message || "Failed to load report");
            })
            .catch(() => ToastMsg("error", "Failed to load report"))
            .finally(() => setLoading(false));
    }, [datePreset, customFrom, customTo, accountId, level, statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => { load(); }, [load]);

    const rows = data?.data || [];
    const cohorts = data?.cohorts || [];
    const s = data?.summary;
    const wired = data?.attribution?.is_wired;
    const maxLag = Math.max(...rows.map((r) => r.median_days_to_won || 0), 1);

    return (
        <ContainerCard>
            <div className="p-2 space-y-4">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
                    <div>
                        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
                            <Timer className="h-6 w-6 text-primary" />
                            Time to Revenue
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            How long each ad's leads take to reach a demo and a signature — not just how many arrive.
                        </p>
                    </div>
                    <Button size="sm" variant="outline" onClick={load} disabled={loading}>
                        <RefreshCw className={cn("h-4 w-4 mr-1.5", loading && "animate-spin")} />
                        Refresh
                    </Button>
                </div>

                <AdFilterBar
                    {...{ datePreset, setDatePreset, customFrom, setCustomFrom, customTo, setCustomTo,
                        accountId, setAccountId, accounts: data?.filters?.accounts || [] }}
                >
                    <StatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
                    <select value={level} onChange={(e) => setLevel(e.target.value)}
                        className="h-9 rounded-md border border-input bg-background px-2 text-sm text-foreground">
                        <option value="ad">Per ad</option>
                        <option value="campaign">Per campaign</option>
                    </select>
                </AdFilterBar>

                <AttributionBanner attribution={data?.attribution} />

                {wired && s && (
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <StatTile tone="leads" label="Attributed leads" value={num(s.leads)}
                            sub={`across ${s.entities} ${level === "ad" ? "ads" : "campaigns"}`} />
                        <StatTile label="Median lead → demo" value={days(s.median_days_to_demo)} />
                        <StatTile tone="cost" label="Median lead → won" value={days(s.median_days_to_won)} />
                        <StatTile label="Deals closed" value={num(s.won)} />
                    </div>
                )}

                {loading && (
                    <div className="flex items-center justify-center py-16 text-muted-foreground">
                        <Loader2 className="h-6 w-6 animate-spin" />
                    </div>
                )}

                {!loading && wired && rows.length === 0 && (
                    <EmptyState title="No attributed leads in this period" />
                )}

                {!loading && wired && rows.length > 0 && (
                    <>
                        <div className="overflow-x-auto rounded-lg border border-border">
                            <table className="w-full text-xs">
                                <thead className="bg-muted/50">
                                    <tr className="text-muted-foreground">
                                        <th className="px-3 py-2.5 text-left font-medium min-w-[190px]">
                                            {level === "ad" ? "Ad" : "Campaign"}
                                        </th>
                                        <th className="px-2 py-2.5 text-right font-medium">Leads</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Won</th>
                                        <th className="px-2 py-2.5 text-left font-medium min-w-[130px]">Lead → demo → won</th>
                                        <th className="px-2 py-2.5 text-right font-medium">→ Demo</th>
                                        <th className="px-2 py-2.5 text-right font-medium">→ Won</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Fastest</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Slowest</th>
                                        <th className="px-2 py-2.5 text-right font-medium">CPL</th>
                                        <th className="px-2 py-2.5 text-right font-medium">Spend / day to revenue</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-border">
                                    {rows.map((r) => (
                                        <tr key={r.id} className={cn("hover:bg-accent/30",
                                            !isDelivering(r.effective_status) && "opacity-60 bg-muted/20")}>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-foreground truncate max-w-[155px]" title={r.name}>{r.name}</span>
                                                    <StatusPill status={r.effective_status} compact />
                                                </div>
                                            </td>
                                            <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{num(r.leads)}</td>
                                            <td className="px-2 py-2 text-right text-foreground font-medium tabular-nums">{num(r.won)}</td>
                                            <td className="px-2 py-2">
                                                <LagBar toDemo={r.median_days_to_demo} toWon={r.median_days_to_won} max={maxLag} />
                                            </td>
                                            <td className="px-2 py-2 text-right text-violet-600 dark:text-violet-400 tabular-nums">{days(r.median_days_to_demo)}</td>
                                            <td className="px-2 py-2 text-right text-emerald-600 dark:text-emerald-400 font-medium tabular-nums">{days(r.median_days_to_won)}</td>
                                            <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{days(r.fastest_days_to_won)}</td>
                                            <td className="px-2 py-2 text-right text-muted-foreground tabular-nums">{days(r.slowest_days_to_won)}</td>
                                            <td className="px-2 py-2 text-right text-amber-700 dark:text-amber-400 tabular-nums">{money(r.cpl)}</td>
                                            <td className="px-2 py-2 text-right text-foreground tabular-nums">{money(r.cost_per_day_to_revenue)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {cohorts.length > 0 && (
                            <div className="rounded-xl border border-border bg-card overflow-hidden">
                                <div className="px-4 py-2.5 border-b border-border bg-muted/40">
                                    <h2 className="text-sm font-semibold text-foreground flex items-center gap-2">
                                        <CalendarClock className="h-4 w-4 text-muted-foreground" />
                                        Cohorts by lead month
                                    </h2>
                                </div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead className="bg-muted/30">
                                            <tr className="text-muted-foreground">
                                                <th className="px-3 py-2 text-left font-medium">Month</th>
                                                <th className="px-3 py-2 text-right font-medium">Leads</th>
                                                <th className="px-3 py-2 text-right font-medium">Demos</th>
                                                <th className="px-3 py-2 text-right font-medium">Demo rate</th>
                                                <th className="px-3 py-2 text-right font-medium">Won</th>
                                                <th className="px-3 py-2 text-right font-medium">Win rate</th>
                                                <th className="px-3 py-2 text-right font-medium">Median → won</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-border">
                                            {cohorts.map((c) => (
                                                <tr key={c.month} className="hover:bg-accent/20">
                                                    <td className="px-3 py-2 text-foreground font-medium">{c.month}</td>
                                                    <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(c.leads)}</td>
                                                    <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{num(c.demos)}</td>
                                                    <td className="px-3 py-2 text-right text-muted-foreground tabular-nums">{pct(c.demo_rate, 1)}</td>
                                                    <td className="px-3 py-2 text-right text-foreground tabular-nums">{num(c.won)}</td>
                                                    <td className="px-3 py-2 text-right text-emerald-600 dark:text-emerald-400 tabular-nums">{pct(c.win_rate, 1)}</td>
                                                    <td className="px-3 py-2 text-right text-foreground tabular-nums">{days(c.median_days_to_won)}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <p className="text-[11px] text-muted-foreground">
                            All timings are medians, not averages — one deal that closed after a year would
                            drag an average far past anything a rep would recognise as typical. Recent cohorts
                            naturally show lower win rates: those leads have had less time to close.
                        </p>
                    </>
                )}
            </div>
        </ContainerCard>
    );
}
